import { z } from 'zod';

const calculateAge = (dateOfBirth: string): number => {
  const dob = new Date(dateOfBirth);
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const monthDiff = today.getMonth() - dob.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
    age--;
  }
  return age;
};

export const studentProfileSchema = z.object({
  date_of_birth: z.string().min(1, 'Date of birth is required').refine((date) => {
    const age = calculateAge(date);
    return age >= 18;
  }, 'You must be at least 18 years old to register'),
  gender: z.enum(['male', 'female', 'non_binary', 'prefer_not_to_say'], {
    errorMap: () => ({ message: 'Please select an option' }),
  }),
  year_group: z.string().optional(),
  first_language: z.string().optional(),
  eal: z.boolean().optional(),
  curriculum_type: z.enum(['uk', 'ib', 'us', 'uae', 'other']).optional(),
  speech_language_support: z.string().optional(),
});

export const parentProfileSchema = z.object({
  child_first_name: z.string().min(1, 'Child\'s first name is required'),
  child_last_name: z.string().min(1, 'Child\'s last name is required'),
  child_date_of_birth: z.string().min(1, 'Child\'s date of birth is required').refine((date) => {
    const age = calculateAge(date);
    return age >= 3;
  }, 'Child must be at least 3 years old').refine((date) => {
    const age = calculateAge(date);
    return age <= 18;
  }, 'Child must be 18 years old or younger'),
  year_group: z.string().min(1, 'Year group is required'),
  first_language: z.string().optional(),
  eal: z.boolean().optional(),
  curriculum_type: z.enum(['uk', 'ib', 'us', 'uae', 'other']),
  speech_language_support: z.string().optional(),
});

export const teacherProfileSchema = z.object({
  subject: z.string().min(1, 'Teaching subject is required'),
});

export type StudentProfileData = z.infer<typeof studentProfileSchema>;
export type ParentProfileData = z.infer<typeof parentProfileSchema>;
export type TeacherProfileData = z.infer<typeof teacherProfileSchema>;

// todov2: Core Assessment Schemas

export const MODE_OF_TALK = ['presenting', 'explaining', 'persuading'] as const;
export type ModeOfTalk = typeof MODE_OF_TALK[number];

export const SETTING_TYPE = ['solo', 'pair', 'group'] as const;
export type SettingType = typeof SETTING_TYPE[number];

export const BAND_LEVEL = ['emerging', 'developing', 'growing', 'effective', 'accomplished'] as const;
export type BandLevel = typeof BAND_LEVEL[number];

export const STRAND_TYPES = ['physical', 'linguistic', 'cognitive', 'social_emotional'] as const;
export type StrandType = typeof STRAND_TYPES[number];

export const taskContextMetadataSchema = z.object({
  year_group: z.string().min(1, 'Year group is required'),
  mode_of_talk: z.enum(MODE_OF_TALK),
  prompt: z.string().min(1, 'Prompt/topic is required'),
  setting: z.enum(SETTING_TYPE),
  time_limit_seconds: z.number().min(30).max(600),
});

export const languageProfileSchema = z.object({
  is_eal: z.boolean().default(false),
  is_bilingual: z.boolean().default(false),
  is_multilingual: z.boolean().default(false),
  self_reported_proficiency: z.enum(['beginner', 'intermediate', 'advanced', 'fluent']).optional(),
  first_language: z.string().optional(),
  languages_spoken: z.array(z.string()).optional(),
});

export const accommodationsSchema = z.object({
  has_accomodations: z.boolean().default(false),
  accommodation_details: z.string().optional(),
  modified_time_limit: z.number().optional(),
  additional_support: z.array(z.string()).optional(),
});

export const consentFlagsSchema = z.object({
  consent_obtained: z.boolean().default(false),
  consent_date: z.string().optional(),
  consent_by: z.string().optional(),
  video_allow_use: z.boolean().default(false),
  anonymised_research: z.boolean().default(false),
});

export const subskillScoreSchema = z.object({
  score: z.number().min(0).max(100),
  band: z.enum(BAND_LEVEL),
  confidence: z.number().min(0).max(1),
  justification: z.string(),
  evidence_clips: z.array(z.string()).optional(),
});

export const strandScoreSchema = z.object({
  band: z.enum(BAND_LEVEL),
  score: z.number().min(0).max(100),
  confidence: z.number().min(0).max(1),
  justification: z.string(),
  subskills: z.record(subskillScoreSchema),
});

export const evidenceClipSchema = z.object({
  id: z.string(),
  start_time: z.number(),
  end_time: z.number(),
  duration: z.number(),
  transcript_excerpt: z.string(),
  strand: z.enum(STRAND_TYPES),
  justification: z.string(),
  teacher_verified: z.boolean().default(false),
});

export const feedbackItemSchema = z.object({
  id: z.string(),
  text: z.string(),
  linked_to_mode: z.enum(MODE_OF_TALK),
  linked_to_strand: z.enum(STRAND_TYPES).optional(),
});

export const studentGoalSchema = z.object({
  id: z.string(),
  text: z.string(),
  student_friendly: z.boolean().default(true),
  target_mode: z.enum(MODE_OF_TALK).optional(),
});

export const multimodalSignalsSchema = z.object({
  physical_delivery: z.object({
    pace: z.number().min(0).max(1),
    pauses: z.number().min(0).max(1),
    fillers: z.number().min(0).max(1),
    volume_variability: z.number().min(0).max(1),
    rhythm: z.number().min(0).max(1),
    confidence_markers: z.number().min(0).max(1),
    gaze_orientation: z.number().min(0).max(1).optional(),
    face_orientation: z.number().min(0).max(1).optional(),
    posture_stability: z.number().min(0).max(1).optional(),
  }).optional(),
  interaction_markers: z.object({
    turn_taking_balance: z.number().min(0).max(1).optional(),
    interruptions: z.number().min(0).optional(),
    overlaps: z.number().min(0).optional(),
    responsiveness: z.number().min(0).max(1).optional(),
    question_asking: z.number().min(0).optional(),
    building_on_others: z.number().min(0).optional(),
  }).optional(),
  linguistic_markers: z.object({
    clarity: z.number().min(0).max(1),
    vocabulary_variety: z.number().min(0).max(1),
    sentence_complexity: z.number().min(0).max(1),
    register_fit: z.number().min(0).max(1),
  }).optional(),
  cognitive_markers: z.object({
    structure: z.number().min(0).max(1),
    reasons_examples: z.number().min(0).max(1),
    counterpoints: z.number().min(0).max(1),
    coherence: z.number().min(0).max(1),
    relevance: z.number().min(0).max(1),
  }).optional(),
  social_emotional_markers: z.object({
    collaboration_signals: z.number().min(0).max(1),
    encouragement: z.number().min(0).max(1),
    respectful_disagreement: z.number().min(0).max(1),
    inclusive_turn_giving: z.number().min(0).max(1),
  }).optional(),
  confidence_indicator: z.number().min(0).max(1).optional(),
  quality_warnings: z.array(z.string()).optional(),
});

export const teacherVerificationSchema = z.object({
  teacher_id: z.string(),
  teacher_name: z.string(),
  verified_at: z.string(),
  score_changes: z.record(z.record(z.string())).optional(),
  evidence_changes: z.record(z.array(z.string())).optional(),
  feedback_changes: z.record(z.array(z.string())).optional(),
  teacher_notes: z.string().optional(),
  original_ai_scores: z.record(strandScoreSchema).optional(),
});

export const todov2AssessmentOutputSchema = z.object({
  id: z.string(),
  assessment_id: z.string(),
  scores: z.object({
    physical: strandScoreSchema,
    linguistic: strandScoreSchema,
    cognitive: strandScoreSchema,
    social_emotional: strandScoreSchema,
    overall_band: z.enum(BAND_LEVEL),
    overall_score: z.number().min(0).max(100),
  }),
  evidence: z.array(evidenceClipSchema),
  feedback: z.object({
    strengths: z.array(feedbackItemSchema),
    next_steps: z.array(feedbackItemSchema),
    goals: z.array(studentGoalSchema),
  }),
  multimodal_signals: multimodalSignalsSchema,
  teacher_verification: teacherVerificationSchema.optional(),
  generated_at: z.string(),
  ai_model: z.string().optional(),
  confidence_score: z.number().min(0).max(1).optional(),
});

export type TaskContextMetadata = z.infer<typeof taskContextMetadataSchema>;
export type LanguageProfile = z.infer<typeof languageProfileSchema>;
export type Accommodations = z.infer<typeof accommodationsSchema>;
export type ConsentFlags = z.infer<typeof consentFlagsSchema>;
export type StrandScore = z.infer<typeof strandScoreSchema>;
export type EvidenceClip = z.infer<typeof evidenceClipSchema>;
export type FeedbackItem = z.infer<typeof feedbackItemSchema>;
export type StudentGoal = z.infer<typeof studentGoalSchema>;
export type MultimodalSignals = z.infer<typeof multimodalSignalsSchema>;
export type TeacherVerification = z.infer<typeof teacherVerificationSchema>;
export type Todov2AssessmentOutput = z.infer<typeof todov2AssessmentOutputSchema>;
